import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Combobox } from "components/elements";
import { ClientUser } from "@compass/shared/types";

export const MultiUserSelector = ({
    value,
    onChange,
    users,
    className,
    inputClassName,
}: {
    value: number[];
    onChange: (value: number[] | null | undefined) => void;
    users: ClientUser[];
    className?: string;
    inputClassName?: string;
}) => {
    const [selectedUsers, setSelectedUsers] = useState<number[]>(value || []);

    // Sync with external value changes (like when organization changes)
    useEffect(() => {
        setSelectedUsers(value || []);
    }, [value]);

    const getSelectedUserNames = () => {
        return selectedUsers
            .map((id) => {
                const user = users.find((u) => u.id === id);
                return user ? user.name || user.email : null;
            })
            .filter(Boolean);
    };

    const filteredUsers = (query: string) => {
        const availableUsers = users.filter((user) => !selectedUsers.includes(user.id));

        const queriedUsers =
            query === ""
                ? availableUsers
                : availableUsers.filter(
                      (user) =>
                          user.firstName?.toLowerCase().includes(query.toLowerCase()) ||
                          user.lastName?.toLowerCase().includes(query.toLowerCase()) ||
                          user.email.toLowerCase().includes(query.toLowerCase()),
                  );

        return queriedUsers.map((user) => ({
            value: user.id,
            display: user.name || user.email,
        }));
    };

    const handleUserSelect = (userId: number | null | undefined) => {
        if (userId && !selectedUsers.includes(userId)) {
            const newSelected = [...selectedUsers, userId];
            setSelectedUsers(newSelected);
            onChange(newSelected);
        }
    };

    const removeUser = (userId: number) => {
        const newSelected = selectedUsers.filter((id) => id !== userId);
        setSelectedUsers(newSelected);
        onChange(newSelected.length > 0 ? newSelected : null);
    };

    return (
        <div className={className}>
            {/* Selected Users Display */}
            {selectedUsers.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1">
                    {selectedUsers.map((userId) => {
                        const user = users.find((u) => u.id === userId);
                        const displayName = user ? user.name || user.email : "";

                        return (
                            <div
                                key={userId}
                                className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800"
                            >
                                <span>{displayName}</span>
                                <button
                                    type="button"
                                    onClick={() => removeUser(userId)}
                                    className="hover:text-blue-600"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* User Selector */}
            <Combobox
                placeholder={selectedUsers.length > 0 ? "Add another user..." : "Select users"}
                className={className}
                inputClassName={inputClassName}
                value={null} // Always null since we handle selection separately
                withClear={false}
                onChange={handleUserSelect}
                displayValue={() => ""} // Always empty since we show selected users above
                options={filteredUsers}
            />
        </div>
    );
};
